/* shellback — one breath, eight frames. The armour opens and shuts.
 *
 * WHAT THE TWO ORIGINAL FRAMES ACTUALLY ENCODE. Put S.shellback_0 and
 * S.shellback_1 side by side and the difference is one row. Frame 0 is nine
 * rows tall with a dark SEAM row between the light band and the lower plate,
 * and its base is inset a pixel at each end. Frame 1 is eight rows, that seam
 * row gone, base spread flat across all sixteen. So the pair is not two walk
 * poses — it is OPEN and SHUT, and the seam row IS the height difference. The
 * shell is a row taller when it is a row open. That one fact drives everything
 * below, and both originals are kept here byte-for-byte as phases 0 and 4.
 *
 * THE BODY IS A STACK, NOT A DRAWING. Every frame is the same seven parts in
 * the same order, and animating means changing which parts are present and how
 * wide they are — never sliding the whole body, which rewrites every band
 * boundary at once and spends the entire motion budget in one transition:
 *
 *   crown     the bare outline row across the top          8 / 10 / 12 / 14 wide
 *   shoulder  a light 'n' band under it, or SWALLOWED      12 / 14 wide
 *   ridge     the first dark plate                         14 / 16 wide
 *   bands     three or four interior rows, the seam        always 16
 *   legs      the row carrying the two 'e' lights          always 16
 *   rim       ground contact, row 14 in all eight frames   14 / 16 wide
 *
 * Which gives three silhouettes the cycle moves between:
 *
 *   A  nine rows: crown, shoulder, ridge, FOUR bands.   (phases 0, 7)
 *   B  eight rows: the shoulder is swallowed, four bands survive. (1, 2, 6)
 *   C  eight rows: the shoulder is back, a BAND row is gone instead. (3, 4, 5)
 *
 * WHY THE PREVIOUS VERSION BOILED. It had the stack right but not the width.
 * Its ink ran 128, 118, 116, 122, 116, 114, 118, 128 — a 11.9% swing, so the
 * creature deflated by ten pixels for six frames of every eight and re-inflated
 * at the ends. The cause: losing a row costs 12-16 px and nothing was paid back.
 *
 * ARMOUR DOES NOT EVAPORATE WHEN IT SETTLES — IT BULGES SIDEWAYS. So the trade
 * is now explicit and it is the same trade every time. WHENEVER A ROW IS
 * SWALLOWED, THE PARTS THAT REMAIN SPREAD A COLUMN PER SIDE:
 *
 *   A -> B   shoulder row gone      -12    crown 10->14  +4
 *            (phases 0->1, 6->7)           ridge 14->16  +2
 *                                          rim   14->16  +2      net -2
 *   B -> C   a band row gone        -16    shoulder back +14
 *            (phases 2->3, 5->6)           crown 14->12  -2      net -4
 *
 * Nothing is added for its own sake; the pixels a compression takes off the
 * height come back as rim, ridge and crown width in the same frame. Phase 4 is
 * the one place the shell also draws IN — crown, shoulder and ridge each pull
 * back a column — because that is the authored shut pose, and it is the loop's
 * single lightest frame rather than six of them.
 *
 *   0  open, arched, nine rows. Seam wide, rim inset, up off the ground.
 *      (original _0, byte-for-byte)
 *   1  the shoulder is swallowed. The top edge drops a row and the crown,
 *      ridge and rim all spread to pay for it — the shell lands OPEN and WIDE,
 *      seam still four rows deep, before the weight has finished arriving.
 *   2  the plates slide: the light band and the dark band swap places and the
 *      seam pinches to six. Crown draws back in to twelve; the leg lights hold.
 *   3  the shoulder pushes back out to fourteen and the LOWEST band row is
 *      swallowed in its place. Ridge holds the full sixteen.
 *   4  shut. The squash releases and crown, shoulder and ridge each pull in a
 *      column: the tightest, lightest frame of the loop, one solid mass on its
 *      full base. This is the frame a hit bounces off. (original _1)
 *   5  the crack. The silhouette pushes back out to the phase-3 width while the
 *      bottom plate opens to six — the interior moves before the outline does.
 *   6  the band row grows back under the ridge, the shoulder is swallowed for
 *      it, and the crown spreads to fourteen. Widest and flattest the shell
 *      gets, and the frame it pushes off from.
 *   7  up. Nine rows, the shoulder returns, the crown pinches back to eight and
 *      the rim lifts off both ends. The bands are still carrying phase 6's
 *      pattern, so the loop closes on the plates settling rather than on the
 *      silhouette arriving.
 *
 * The down half swallows the SHOULDER first and a band row second; the up half
 * does it in the other order. That is what keeps 4->0 from being 0->4 reversed
 * — not the seam, which is a centred opening in both directions and cannot
 * close "outside in" and open "from the middle" the way an earlier draft of
 * this comment claimed.
 *
 * MEASURED, not asserted:
 *   ink    128, 126, 124, 122, 116, 122, 126, 128   median 126, swing 9.5%
 *   steps  32, 20, 38, 32, 22, 36, 26, 22 px        median 32, spread 1.19x
 *   every frame's row widths are non-decreasing top-down (no silhouette pinch)
 *   base row never leaves row 14; nothing translates horizontally
 */

// phase 0 — full arch, seam wide open, up on the rim. The vulnerable pose.
// Silhouette A. Byte-for-byte S.shellback_0. (keyframe)
export const shellback_0 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '....oooooooo....',
  '..oonnnnnnnnoo..',
  '.onNNNNNNNNNNno.',
  'onNnnnnnnnnnnNno',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'onNNNNNNNNNNNNno',
  'oonneennnneennoo',
  '.oooooooooooooo.',
  '................',
];

// phase 1 — silhouette B. The shoulder row is swallowed and paid for on the
// spot: crown 10->14, ridge 14->16, rim 14->16. The top edge drops without the
// seam closing, so the armour lands open and a column wider on every side.
export const shellback_1 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '.oooooooooooooo.',
  'onNNNNNNNNNNNNno',
  'onNnnnnnnnnnnNno',
  'onNnNNNNNNNNnNno',
  'onNNnnnnnnnnNNno',
  'onNNNNNNNNNNNNno',
  'ooneennnnnneenoo',
  'oooooooooooooooo',
  '................',
];

// phase 2 — silhouette B, crown drawn in to twelve. The plates slide: the light
// and dark bands swap places and the seam pinches from eight to six. Weight is
// fully down, so the rim keeps the whole sixteen.
export const shellback_2 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '..oooooooooooo..',
  'onNNNNNNNNNNNNno',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'onNNNnnnnnnNNNno',
  'onNNNNNNNNNNNNno',
  'ooneennnnnneenoo',
  'oooooooooooooooo',
  '................',
];

// phase 3 — silhouette C. The shoulder pushes back out to fourteen and the
// lowest band row is swallowed to make room for it; the ridge holds the full
// sixteen. Same eight rows as phase 2, a different eight rows.
export const shellback_3 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '......oooo......',
  '..oonnnnnnnnoo..',
  '.onNNNNNNNNNNno.',
  'onNnnnnnnnnnnNno',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'onNNNNNNNNNNNNno',
  'oonneennnneennoo',
  '.oooooooooooooo.',
  '................',
];

// phase 4 — shut. Silhouette C with crown, shoulder and ridge each pulled in a
// column: the seam row is gone, the band has dropped onto the plate and the
// shell is one solid mass on its full base. The lightest frame of the loop and
// the frame a hit bounces off. Byte-for-byte S.shellback_1. (keyframe)
export const shellback_4 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '..oonnnnnnnnoo..',
  '.onNNNNNNNNNNno.',
  'onNNNNNNNNNNNNno',
  'onNnnnnnnnnnnNno',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'oonnneenneennnoo',
  '.oooooooooooooo.',
  '................',
];

// phase 5 — the crack. The silhouette pushes back out to the phase-3 width
// while the bottom plate opens to six and the leg lights draw together. The
// interior has moved and the outline has not; that is the tell for the lift.
export const shellback_5 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '......oooo......',
  '..oonnnnnnnnoo..',
  '.onNNNNNNNNNNno.',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'onNNNNNNNNNNNNno',
  'onNnnnnnnnnnnNno',
  'oonneennnneennoo',
  '.oooooooooooooo.',
  '................',
];

// phase 6 — silhouette B again, crown at its widest fourteen. The band row
// grows back under the ridge and the shoulder is swallowed to pay for it — the
// reverse order to the way down. Widest and flattest, and the push-off frame.
export const shellback_6 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '.oooooooooooooo.',
  'onNNNNNNNNNNNNno',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'onNNnnnnnnnnNNno',
  'onNNNNNNNNNNNNno',
  'oonnneenneennnoo',
  'oooooooooooooooo',
  '................',
];

// phase 7 — up. Silhouette A: nine rows, the shoulder returns, the crown
// pinches back to eight and the rim lifts off both ends. The bands still carry
// phase 6's pattern, so the loop closes on the plates settling rather than on
// the silhouette arriving.
export const shellback_7 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '....oooooooo....',
  '..oonnnnnnnnoo..',
  '.onNNNNNNNNNNno.',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'onNNnnnnnnnnNNno',
  'onNNNNNNNNNNNNno',
  'oonnneenneennnoo',
  '.oooooooooooooo.',
  '................',
];
